import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, Paper, Stack } from "@mui/material";
import { useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import RevaText from "components/common/RevaText";
import performRequest from "../../../network/perform-request";
import {
  competitionMatchScoringRoute,
  competitionMatchScoringUpdateRoute,
  competitionsShowRoute,
  tournamentMatchScoringPresetsRoute,
} from "../../../network/api-routes";
import MatchScoringForm from "./MatchScoringForm";
import { DEFAULT_MATCH_SCORING } from "./matchScoring";

const CompetitionMatchScoring = ({ userContext }) => {
  const intl = useIntl();
  const { id: competitionUuid } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const user = userContext?.user;

  const [competitionId, setCompetitionId] = useState(null);
  const [settings, setSettings] = useState(null);
  const [presets, setPresets] = useState([]);

  // When the event has no rule of its own, each tournament uses its sport's default:
  // one read-only option per distinct default (usually a single one).
  const inheritedOptions = useMemo(() => {
    const generic = intl.formatMessage({ id: "matchScoring.sportDefaultGeneric" });
    const groups = settings?.sport_defaults || [];
    if (groups.length === 0) return [{ key: "classic", label: generic, config: DEFAULT_MATCH_SCORING }];

    return groups.map((group) => {
      const sports = group.sports.join(" / ");
      return {
        key: group.preset,
        label: sports ? intl.formatMessage({ id: "matchScoring.sportDefault" }, { sports }) : generic,
        tabLabel: intl.formatMessage(
          { id: "matchScoring.sportTab" },
          { sports: sports || generic, count: group.tournaments_count },
        ),
        config: group.config,
      };
    });
  }, [settings, intl]);

  const fetchSettings = useCallback(
    async (id) => {
      const res = await performRequest("POST", competitionMatchScoringRoute, { competition_id: id }, true, user);
      setSettings(res.data.data);
    },
    [user],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const competitionRes = await performRequest(
          "POST",
          competitionsShowRoute,
          { competition_uuid: competitionUuid },
          true,
          user,
        );
        const id = competitionRes.data.data.id;
        const presetsRes = await performRequest("POST", tournamentMatchScoringPresetsRoute, {}, true, user);
        if (cancelled) return;
        setCompetitionId(id);
        setPresets(presetsRes.data.data || []);
        await fetchSettings(id);
      } catch (e) {
        console.error(e);
        enqueueSnackbar(intl.formatMessage({ id: "matchScoring.loadError" }), { variant: "error" });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competitionUuid]);

  const handleSave = async (matchScoring) => {
    try {
      const res = await performRequest(
        "POST",
        competitionMatchScoringUpdateRoute,
        { competition_id: competitionId, match_scoring: matchScoring },
        true,
        user,
      );
      setSettings(res.data.data);
      enqueueSnackbar(res.data.message || intl.formatMessage({ id: "matchScoring.saved" }), { variant: "success" });
    } catch (e) {
      console.error(e);
      enqueueSnackbar(e?.response?.data?.message || intl.formatMessage({ id: "No se pudieron guardar los cambios" }), {
        variant: "error",
      });
    }
  };

  if (!settings) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      <Box>
        <RevaText id="Puntos por partido" variant="h2" />
        <RevaText id="matchScoring.eventDescription" variant="body2" color="text.secondary" />
        {settings.overriding_tournaments?.length > 0 && (
          <RevaText variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {intl.formatMessage(
              { id: "matchScoring.overridesNote" },
              {
                count: settings.overriding_tournaments.length,
                names: settings.overriding_tournaments.map((tournament) => tournament.name).join(", "),
              },
            )}
          </RevaText>
        )}
      </Box>

      <Paper sx={{ p: 3 }}>
        <MatchScoringForm
          matchScoring={settings.match_scoring}
          presets={presets}
          inheritedOptions={inheritedOptions}
          inheritedBadgeId="matchScoring.sportRulesActive"
          inheritedNoteId="matchScoring.mixedSportsNote"
          inheritLabelId="matchScoring.inheritSport"
          inheritHelpId="matchScoring.inheritSportHelp"
          lockedMessage={
            settings.locked_by
              ? intl.formatMessage({ id: "matchScoring.eventLocked" }, { tournament: settings.locked_by.name })
              : null
          }
          hasClosedGroupMatches={settings.has_closed_group_matches}
          onSave={handleSave}
        />
      </Paper>
    </Stack>
  );
};

export default CompetitionMatchScoring;
